/**
 * Copyable error-report card surfaced inside the error variant of both
 * RC dialogs (`<ClickToDialDialog />` + `<SendSmsDialog />`).
 *
 * Renders a monospace code block with everything an engineer needs to
 * diagnose the failure without first asking the rep for more info:
 *
 *   - timestamp (backend's UTC stamp of the failure)
 *   - error_code + message (structured from the backend)
 *   - rc_status_code (raw RC HTTP status; null when SDK threw without
 *     a response)
 *   - attempt_id (PK of the `rc_outbound_attempts` audit row — direct
 *     query key)
 *   - rep email + RC extension (from useProfileData)
 *   - deal_id + todo_key + to_number (dialog props)
 *
 * The Copy button writes the same text to the clipboard, ready to
 * paste into Slack. Engineers correlate by attempt_id (single
 * SELECT) or timestamp (log scan).
 */

import { useCallback, useState } from "react";
import { CheckIcon, ClipboardIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useProfileData } from "@/hooks/use-profile-data";

export interface RcErrorReportProps {
  errorCode: string;
  message?: string;
  attemptId?: string;
  timestamp?: string;
  /** Raw RC HTTP status, or `null` when the SDK threw with no HTTP response. */
  rcStatusCode?: number | null;
  // Dialog-side context
  dealId?: number | null;
  todoKey?: string | null;
  toNumber: string;
  /** The RingOut session that ended early (the live-status failure), when there is one. */
  rcSessionId?: string | null;
  /** The phone RingCentral was asked to ring first, when the failure is about it. */
  fromNumber?: string | null;
}

function formatReport(
  props: RcErrorReportProps,
  repEmail: string | null,
  rcExtensionId: string | null,
): string {
  // Padding to keep the field column aligned in monospace — the
  // longest label is `rc_status_code:` (15 chars including colon).
  // Fields are listed in triage-priority order: temporal first
  // (timestamp + status), then structured codes, then context.
  const lines = [
    "ERROR REPORT",
    `timestamp:      ${props.timestamp ?? "(unknown)"}`,
    `error_code:     ${props.errorCode}`,
    `rc_status:      ${props.rcStatusCode ?? "(none — SDK threw without response)"}`,
    `message:        ${props.message ?? "(none)"}`,
    `attempt_id:     ${props.attemptId ?? "(none)"}`,
    `rep:            ${repEmail ?? "(unknown)"}`,
    `rc_extension:   ${rcExtensionId ?? "(unknown)"}`,
    `deal_id:        ${props.dealId ?? "(none)"}`,
    `todo_key:       ${props.todoKey ?? "(none)"}`,
    `to_number:      ${props.toNumber}`,
    ...(props.rcSessionId ? [`rc_session_id:  ${props.rcSessionId}`] : []),
    ...(props.fromNumber ? [`from_number:    ${props.fromNumber}`] : []),
  ];
  return lines.join("\n");
}

export function RcErrorReport(props: RcErrorReportProps) {
  const { data: profileData } = useProfileData();
  const [copied, setCopied] = useState(false);

  const report = formatReport(
    props,
    profileData?.email ?? null,
    profileData?.rc_extension_id ?? null,
  );

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(report).then(() => {
      setCopied(true);
      // Brief confirmation indicator. 1.5s is enough for the rep to
      // see it before pasting; doesn't block them switching apps.
      setTimeout(() => setCopied(false), 1500);
    });
  }, [report]);

  return (
    <div className="mt-3 rounded-md border bg-muted/50">
      <div className="flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          For engineers
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <CheckIcon className="h-3 w-3" />
              Copied
            </>
          ) : (
            <>
              <ClipboardIcon className="h-3 w-3" />
              Copy
            </>
          )}
        </Button>
      </div>
      <pre className="overflow-x-auto px-3 py-2 text-[11px] leading-relaxed font-mono whitespace-pre">
        {report}
      </pre>
    </div>
  );
}
